import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import {
	API_DATE_FORMAT,
	admissionDateInRange,
	getAdmissionDateMinimum,
} from '@/utils/dateHelper';
import {
	useGetNextFileNoQuery,
	useGetNextFileNoStringMutation,
	useRegisterProgramMutation,
} from '@/api/member-management/admissionRequest';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppGetNextNumber from '@/common/components/form-inputs/AppGetNextNumber';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	useGetAllEnumListQuery,
	useGetDiagnosisTreeListQuery,
	useGetDicListQuery,
} from '@/api/common';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import dayjs from 'dayjs';
import FamilyMemberLinkCheck from './FamilyMemberLinkCheck';
import { useRegisterDuplicationCheck } from './useRegisterDuplicationCheck';
import PendingClosedMemberAlert from '@/common/components/member-duplication/PendingClosedMemberAlert';
import PendingFamilyMemberLinkAlert from '@/common/components/identity-link/PendingFamilyMemberLinkAlert';
import { FamilyMemberDuplicationCheckDTO } from '@/api/DTO/family/family.interface';
import { useLoginAccountRegisterPrompt } from '@/common/components/program-app-login/useLoginAccountRegisterPrompt';
import i18n from '@/i18n';

interface Props {
	trigger: number;
	details: any;
}

const NewProgramMemberDialog = (props: Props) => {
	const { trigger, details } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { familyTrigger, duplicationDialogs, pendingClosedMember, linkFields } =
		useRegisterDuplicationCheck(trigger, details);
	const [linkFamilyMember, setLinkFamilyMember] =
		useState<FamilyMemberDuplicationCheckDTO | null>(null);
	const [yearList, setYearList] = useState<any>([]);
	const [registerProgram] = useRegisterProgramMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const [getNextFileNoString] = useGetNextFileNoStringMutation();

	const navigate = useNavigate();
	const promptLoginAccount = useLoginAccountRegisterPrompt();

	const { data: diagnosisTreeList } = useGetDiagnosisTreeListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: nextFileNo } = useGetNextFileNoQuery(null, {
		refetchOnMountOrArgChange: true,
		skip: !open,
	});

	const { data: suspectedMentalHealthProblemDicList } = useGetDicListQuery(
		'suspected_mental_health_problem',
		{ refetchOnMountOrArgChange: true }
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		admissionDate: Yup.string()
			.required(t('errorMessage.required'))
			.test(admissionDateInRange(t('errorMessage.admissionDateOutOfRange'))),
		diagnosisStatus: Yup.string().required(t('errorMessage.pleaseSelect')),

		mainDiagnosis: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis != '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),

		mainDiagnosisSpecify: Yup.string().when('mainDiagnosis', {
			is: (mainDiagnosis: string) => mainDiagnosis && mainDiagnosisIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),

		secondaryDiagnosis: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis == '1',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		secondaryDiagnosisSpecify: Yup.string().when('secondaryDiagnosis', {
			is: (secondaryDiagnosis: string) =>
				secondaryDiagnosis && secondaryDiagnosisIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),

		suspectedMentalHealthProblem: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis == '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		suspectedMentalHealthProblemSpecify: Yup.string().when(
			'suspectedMentalHealthProblem',
			{
				is: (suspectedMentalHealthProblem: string) =>
					suspectedMentalHealthProblem && suspectedMentalHealthProblemIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}
		),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		if (details) {
			methods.setValue('diagnosisStatus', details.diagnosisStatus);
			methods.setValue('mainDiagnosis', details.mainDiagnosis);
			methods.setValue('mainDiagnosisSpecify', details.mainDiagnosisSpecify);
			methods.setValue('secondaryDiagnosis', details.secondaryDiagnosis);
			methods.setValue(
				'secondaryDiagnosisSpecify',
				details.secondaryDiagnosisSpecify
			);
			methods.setValue(
				'suspectedMentalHealthProblem',
				details.suspectedMentalHealthProblem
			);
			methods.setValue(
				'suspectedMentalHealthProblemSpecify',
				details.suspectedMentalHealthProblemSpecify
			);
		}
	}, [details]);

	const watchMainDiagnosis = methods.watch('mainDiagnosis');
	const watchDiagnosisStatus = methods.watch('diagnosisStatus');
	const watchSecondaryDiagnosis = methods.watch('secondaryDiagnosis');
	const watchSuspectedMentalHealthProblem = methods.watch(
		'suspectedMentalHealthProblem'
	);

	useEffect(() => {
		if (nextFileNo && open) {
			const parts = nextFileNo.data.split('/');
			methods.setValue('fileNoA', `${parts[0]}/${parts[1]}`);
			methods.setValue('fileNoB', parts[2]);
			methods.setValue('fileNoC', parts[3]);
			const years = parts[2].split('-');
			setYearList([
				{
					id: `${years[0] - 1}-${years[0]}`,
					name: `${years[0] - 1}-${years[0]}`,
				},
				{ id: parts[2], name: parts[2] },
				{
					id: `${years[1]}-${Number(years[1]) + 1}`,
					name: `${years[1]}-${Number(years[1]) + 1}`,
				},
			]);
		}
	}, [nextFileNo, open]);

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
		setLinkFamilyMember(null);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const parts = nextFileNo?.data.split('/');
		const response = await registerProgram({
			...data,
			admissionDate: data.admissionDate,
			id: admissionRequestId,
			fileNo: `${parts[0]}/${parts[1]}/${data.fileNoB}/${data.fileNoC}`,
			...(linkFamilyMember ? { familyMemberId: linkFamilyMember.id } : {}),
			...linkFields(),
		});
		if ('data' in response) {
			enqueueSnackbar(
				t('snackbar.updateMemberSuccess', {
					type: t('member.memberDetailsLabel'),
				}),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
			navigate(`/member-management/member-list/detail/${admissionRequestId}`);
			promptLoginAccount(admissionRequestId || '');
		}
	};

	const mainDiagnosisIsOther = () => {
		const flatDiagnosisList = diagnosisTreeList?.data.flatMap((item: any) => [
			{ ...item },
			...(item.childrenList?.map((sub: any) => ({ ...sub })) || []),
		]);
		const obj = (flatDiagnosisList || []).find(
			(item: any) => item.id == watchMainDiagnosis
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};

	const secondaryDiagnosisIsOther = () => {
		const flatDiagnosisList = diagnosisTreeList?.data.flatMap((item: any) => [
			{ ...item },
			...(item.childrenList?.map((sub: any) => ({ ...sub })) || []),
		]);
		const obj = (flatDiagnosisList || []).find(
			(item: any) => item.id == watchSecondaryDiagnosis
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};
	const suspectedMentalHealthProblemIsOther = () => {
		const obj = suspectedMentalHealthProblemDicList?.data.find(
			(item: any) => item.id == watchSuspectedMentalHealthProblem
		);
		if (obj && obj.code == '8') return true;
		return false;
	};

	return (
		<>
			{duplicationDialogs}
			<FamilyMemberLinkCheck
				trigger={familyTrigger}
				details={details}
				setLastOpen={setOpen}
				setLinkFamilyMember={setLinkFamilyMember}
			/>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='md'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{details?.linkedClosedUserId ||
					details?.linkedLegacyFileNo ||
					pendingClosedMember
						? t('button.reRegisterProgram')
						: t('button.newProgramMember')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					{pendingClosedMember && (
						<PendingClosedMemberAlert closedMember={pendingClosedMember} />
					)}
					{linkFamilyMember && (
						<PendingFamilyMemberLinkAlert familyMember={linkFamilyMember} />
					)}
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.registerMemberText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={1.8}>
									<AppTextField
										control={methods.control}
										name='fileNoA'
										label={''}
										disabled
									/>
								</Grid>
								<span style={{ verticalAlign: 'middle' }}>/</span>
								<Grid size={1.8}>
									<AppDropDownMenu
										control={methods.control}
										name='fileNoB'
										label={''}
										options={yearList}
										handleChange={async (e: any) => {
											if (e) {
												const response = await getNextFileNoString({
													fiscalYear: e,
												});
												if ('data' in response) {
													const parts = response.data?.data.split('/');
													methods.setValue(
														'fileNoA',
														`${parts[0]}/${parts[1]}`
													);
													methods.setValue('fileNoB', parts[2]);
													methods.setValue('fileNoC', parts[3]);
													const years = parts[2].split('-');
													setYearList([
														{
															id: `${years[0] - 1}-${years[0]}`,
															name: `${years[0] - 1}-${years[0]}`,
														},
														{ id: parts[2], name: parts[2] },
														{
															id: `${years[1]}-${Number(years[1]) + 1}`,
															name: `${years[1]}-${Number(years[1]) + 1}`,
														},
													]);
												}
											}
										}}
									/>
								</Grid>
								/
								<Grid size={1.8}>
									<AppGetNextNumber
										control={methods.control}
										name='fileNoC'
										nextFileNo={nextFileNo?.data}
									/>
								</Grid>
								<Grid size={6}></Grid>
								<Grid size={6}>
									<AppDatePicker
										minimumDate={getAdmissionDateMinimum()}
										maximumDate={dayjs().format(API_DATE_FORMAT)}
										control={methods.control}
										name='admissionDate'
										label={t('member.admissionDate') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.diagnosisStatus') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name={`diagnosisStatus`}
										row
										options={(allEnumList?.data.DiagnosisStatusEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('mainDiagnosis', '');
											methods.setValue('mainDiagnosisSpecify', '');
											methods.setValue('secondaryDiagnosis', '');
											methods.setValue('secondaryDiagnosisSpecify', '');
											methods.setValue('suspectedMentalHealthProblem', '');
											methods.setValue(
												'suspectedMentalHealthProblemSpecify',
												''
											);
											methods.trigger('mainDiagnosisSpecify');
											methods.trigger('secondaryDiagnosisSpecify');
											methods.trigger('suspectedMentalHealthProblemSpecify');
										}}
									/>
								</Grid>
								{String(watchDiagnosisStatus) === '0' && (
									<>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name='mainDiagnosis'
												label={
													t('preAdmission.mainDiagnosis') +
													`${watchDiagnosisStatus != 2 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('mainDiagnosisSpecify', '');
													methods.clearErrors('mainDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!mainDiagnosisIsOther()}
												control={methods.control}
												name='mainDiagnosisSpecify'
												label={`${t('preAdmission.mainDiagnosisSpecify')}${
													mainDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
									</>
								)}
								{watchDiagnosisStatus == 1 && (
									<>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name='mainDiagnosis'
												label={
													t('preAdmission.mainDiagnosis') +
													`${watchDiagnosisStatus != 2 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('mainDiagnosisSpecify', '');
													methods.clearErrors('mainDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!mainDiagnosisIsOther()}
												control={methods.control}
												name='mainDiagnosisSpecify'
												label={`${t('preAdmission.mainDiagnosisSpecify')}${
													mainDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
										<Grid size={6}>
											<AppAutoCompleteMenu
												optional={true}
												control={methods.control}
												name='secondaryDiagnosis'
												label={
													t('preAdmission.secondaryDiagnosis') +
													`${watchDiagnosisStatus == 1 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('secondaryDiagnosisSpecify', '');
													methods.clearErrors('secondaryDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!secondaryDiagnosisIsOther()}
												control={methods.control}
												name='secondaryDiagnosisSpecify'
												label={`${t('preAdmission.secondaryDiagnosisSpecify')}${
													secondaryDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
									</>
								)}
								{watchDiagnosisStatus == 2 && (
									<>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='suspectedMentalHealthProblem'
												label={
													t('preAdmission.suspectedMentalHealthProblem') +
													`${watchDiagnosisStatus == 2 ? '*' : ''}`
												}
												options={(
													suspectedMentalHealthProblemDicList?.data || []
												).map((item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												})}
												handleChange={() => {
													methods.setValue(
														'suspectedMentalHealthProblemSpecify',
														''
													);
													methods.clearErrors(
														'suspectedMentalHealthProblemSpecify'
													);
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!suspectedMentalHealthProblemIsOther()}
												control={methods.control}
												name='suspectedMentalHealthProblemSpecify'
												label={`${t(
													'preAdmission.suspectedMentalHealthProblemSpecify'
												)}${suspectedMentalHealthProblemIsOther() ? '*' : ''}`}
											/>
										</Grid>
									</>
								)}
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewProgramMemberDialog;
